import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, Modal } from 'react-bootstrap';
import { Button } from '@/components/ui/button';
import {
  Breadcrumb,
  createRootFolderId,
  PersonalDocumentFilter,
} from '../types';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import {
  PersonalDocumentsTable,
  TableItem as PersonalDocumentRowType,
} from '../../../pages/Documents2/Tables/PersonalDocumentsTable';
import {
  usePersonalDocumentsFilterQuery,
  usePersonalDocumentsQuery,
  useSearchNavigatablesQuery,
} from '../../../__generated__/graphql';
import { SkeletonTable } from '../../../components/Skeleton/Table';
import ServerError from '../../../../../components/ServerError';
import {
  mapNavigatable,
  mapPersonalDocumentPreview,
  mapPersonalDocuments,
} from '../mappings';
import { DocumentPreviewModal } from '../DocumentInfo/DocumentPreviewModal';
import { RowFileTypeEnums } from '../Enums/RowFileTypeEnums';
import { isPreviewableType } from '../DocumentInfo/DocumentPreview';
import { DocumentInfoSidebar } from '../DocumentInfo/DocumentInfoSidebar';
import {
  applyPersonalDocumentsFilter,
  hasPersonalDocumentFilter,
} from '../Fliters/filters';
import { PersonalDocumentsFiltersConnected } from '../Fliters/PersonalDocumentsFiltersConnected';
import { HREmployee } from '../Fliters/mappings';
import { OptionBase } from '../../../../../components/Select/types';
import {
  EmployeeOption,
  mapEmployeeOption,
} from '../../../components/Selects/EmployeeOptionLabel';
import { DocumentCategoryOption, parseValidVisibilityOptions } from '../types';
import { getTranslatedCategoryName } from '../utils/getTranslatedCategoryName';

import { DocumentMoveModal } from '../DocumentMoveModal/DocumentMoveModal';
import { SelectionBottomPanel } from '../../../components/SelectionBottomPanel/SelectionBottomPanel';
import { TableBottomBanner } from './TableBottomBanner';
import { DocumentUploadModalConnected } from '../DocumentUploadModal/DocumentUploadModalConnected';
import { FolderBreadcrumbs } from '../FolderBreadcrumbs';
import {
  useBreadcrumbs,
  useCreateFolder,
  useDocumentDelete,
  useDocumentView,
} from '../hooks';
import { useDocumentMoveTo } from '../hooks/useDocumentMoveTo';
import styled from '@emotion/styled';
import { Theme } from '../../../../../theme';
import { DocumentDeleteConfirmation } from '../DeleteConfirmations';
import { DocumentFolderModal } from '../DocumentFolderModal/DocumentFolderModal';
import { useFolder } from '../hooks/useFolder';
import { useDocumentRename } from '../hooks/useDocumentRename';
import { DocumentRenameModal } from '../DocumentRenameModal/DocumentRenameModal';
import { useUserInfo } from '../../../context/UserInfo';
import GlobalTableFilter from '../../../components/GlobalTableFilter';
import ToggleGroup from '../../../../../components/button/ToggleGroup';
import { useServerFeature } from '../../../../../hooks/useServerFeature';

type Props = {
  employeeId: string;
  filter: PersonalDocumentFilter;
  setFilter: (filter: PersonalDocumentFilter) => void;
};

const i18Path = 'documents2.personal';

export const DocumentPageConnected: React.FC<React.PropsWithChildren<Props>> = (
  props,
) => {
  const { employeeId, filter, setFilter } = props;
  const t = useNamespacedTranslation(i18Path);
  const { id: meId } = useUserInfo();
  const location = useLocation();
  const isManager = meId !== employeeId; // the only person who allow to view other documents is the manager
  const isSelfUploadAllowed = useServerFeature('Self_Upload_Documents');
  const canUpload = isManager || isSelfUploadAllowed;
  const rootFolderId = createRootFolderId(employeeId);

  const { currentFolderId, setCurrentFolderId } = useFolder({
    employeeId,
  });

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string | null>(
    filter.documentName || null,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(filter.documentName || null);
    }, 300);
    return () => clearTimeout(timer);
  }, [filter.documentName]);

  const [searchScope, setSearchScope] = useState<'all' | 'current'>('current');

  useEffect(() => {
    if (!filter.documentName) {
      setSearchScope('current');
    }
  }, [filter.documentName]);

  const {
    data: filterData,
    loading: filterLoading,
    error: filterError,
  } = usePersonalDocumentsFilterQuery();

  const { data, loading, error } = usePersonalDocumentsQuery({
    variables: {
      employeeId,
      folderId: currentFolderId,
    },
  });

  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
  } = useSearchNavigatablesQuery({
    variables: { employeeId, searchTerm: debouncedSearchTerm ?? '' },
    skip: !debouncedSearchTerm || searchScope !== 'all',
  });

  const parentFolderId = data?.navigatable?.parentFolderId
    ? data?.navigatable?.parentFolderId
    : createRootFolderId(employeeId);

  const {
    rowsToMove,
    handleMoveToFolder,
    handleMoveToModalClose,
    setSelectedFolderId,
    selectedFolderId,
    move,
    moveInProgress,
    handleMassMove,
    isMoveModalVisible,
  } = useDocumentMoveTo({ employeeId });

  const {
    setFolderName,
    handleCreateFolderClose,
    createFolderVisible,
    setCreateFolderVisible,
    createFolder,
    createFolderInProgress,
    addDocumentPath,
    folderName,
  } = useCreateFolder({
    currentFolderId:
      isMoveModalVisible && selectedFolderId
        ? selectedFolderId
        : currentFolderId,
    rootFolderId,
    employeeId,
    isManager,
  });

  const [uploadVisible, setUploadVisible] = useState<boolean>(false);
  const [showNoFilesModal, setShowNoFilesModal] = useState(false);
  const {
    sidebarVisible,
    previewVisible,
    setDocumentSidebar,
    setDocumentPreview,
    handlePreviewClose,
    handleSideBarClose,
    handleDownload,
    updateUrl,
  } = useDocumentView();

  const {
    handleDeleteClose,
    handleDelete,
    handleMassDelete,
    documentDelete,
    deleteItems,
    deleteInProgress,
  } = useDocumentDelete({ employeeId });

  const {
    rowToRename,
    renameInProgress,
    name,
    handleSetNewName,
    handleOpenRenameModal,
    handleCloseRenameModal,
    rename,
  } = useDocumentRename();

  const { breadcrumbs, getNextFolderId } = useBreadcrumbs({
    data,
    currentFolderId,
    rootFolderId,
    employeeId,
  });

  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );

  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

  const isSearchAll = !!debouncedSearchTerm && searchScope === 'all';
  const activeLoading = isSearchAll ? searchLoading : loading;

  const allDocuments = useMemo(() => {
    if (isSearchAll) {
      return (searchData?.searchNavigatables ?? []).map((item) =>
        mapNavigatable(item, meId),
      );
    }
    return data ? mapPersonalDocuments(data, meId) : [];
  }, [isSearchAll, searchData, data, meId]);

  const scopeButtons = useMemo(
    () => [
      { name: 'all' as const, title: t('searchScopeAllDocuments') },
      { name: 'current' as const, title: t('searchScopeCurrentFolder') },
    ],
    [t],
  );

  const documents = useMemo(() => {
    if (isSearchAll) {
      // Server already filtered by searchTerm — only apply non-documentName filters
      const filterWithoutName = { ...filter, documentName: null };
      return applyPersonalDocumentsFilter(filterWithoutName, allDocuments);
    }
    return applyPersonalDocumentsFilter(filter, allDocuments);
  }, [isSearchAll, filter, allDocuments]);

  // Check for documentId in URL on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const documentId = searchParams.get('documentId');
    const fileId = searchParams.get('fileId');

    if (documentId && allDocuments.length > 0) {
      const document = allDocuments.find((doc) => doc.id === documentId);
      if (document && document.files?.length) {
        const selectedFile = fileId
          ? document.files.find((file) => file.id === fileId)
          : document.files[0];

        if (selectedFile) {
          setSelectedDocumentId(documentId);
          setSelectedFileId(selectedFile.id);

          // Determine if we should show preview or sidebar based on file type
          if (isPreviewableType(selectedFile.type)) {
            setDocumentPreview(true);
          } else {
            setDocumentSidebar(true);
          }
        } else {
          setSelectedDocumentId(null);
          setSelectedFileId(null);
          updateUrl(null);
        }
      } else {
        setSelectedDocumentId(null);
        setSelectedFileId(null);
        updateUrl(null);
      }
    }
  }, [
    location.search,
    allDocuments,
    setDocumentPreview,
    setDocumentSidebar,
    updateUrl,
  ]);

  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [resetSelectedRows, setResetSelectedRows] = useState<boolean>(false);
  const [selectAllRows, setSelectAllRows] = useState<boolean>(false);

  const addSelectedRow = useCallback((rows?: PersonalDocumentRowType[]) => {
    setSelectedRows(rows);
    setResetSelectedRows(false);
    setSelectAllRows(false);
  }, []);

  const getUniqueSelectedRows = () => {
    return selectedRows.filter(
      (obj: PersonalDocumentRowType, index: number) =>
        selectedRows.findIndex(
          (item: PersonalDocumentRowType) => item.fullPath === obj.fullPath,
        ) === index,
    );
  };

  const handleResetState = () => {
    setResetSelectedRows(true);
  };

  const [categoryOptionsWithId, categoryOptionsWithName] = useMemo<
    [DocumentCategoryOption[], OptionBase[]]
  >(() => {
    if (!filterData) {
      return [[], []];
    }

    return filterData.documentCategories.reduce<
      [DocumentCategoryOption[], OptionBase[]]
    >(
      (options, category) => {
        const translatedName = getTranslatedCategoryName(category);
        options[0].push({
          value: category.Id,
          label: translatedName,
          validVisibilityOptions: parseValidVisibilityOptions(
            category.flair__Valid_Visibility_Options__c,
          ),
        });
        options[1].push({
          value: category.Name,
          label: translatedName,
        });
        return options;
      },
      [[], []],
    );
  }, [filterData]);

  const employeeOptions: EmployeeOption[] = useMemo(() => {
    if (!filterData) {
      return [];
    }

    const activeEmployees = filterData.activeEmployees.map(mapEmployeeOption);
    return [...activeEmployees, HREmployee];
  }, [filterData]);

  const getSelectedDocumentRow = () => {
    const documentRow = selectedDocumentId
      ? allDocuments.find((x) => x.id === selectedDocumentId)
      : null;

    return documentRow
      ? mapPersonalDocumentPreview({
          ...documentRow,
          file: documentRow.files?.find((file) => file.id === selectedFileId),
        })
      : null;
  };

  const onClick = useCallback(
    (row: PersonalDocumentRowType) => {
      const { file, ...document } = row;
      if (document.type.type === RowFileTypeEnums.EmptyEmployeeDocument) {
        setShowNoFilesModal(true);
      } else if (file && !document.subRows) {
        setSelectedDocumentId(file.documentId);
        setSelectedFileId(file.id);

        if (isPreviewableType(file.type)) {
          setDocumentPreview(true);
        } else {
          setDocumentSidebar(true);
        }

        updateUrl(file.documentId, file.id);
      } else if (!file && !document.subRows) {
        setCurrentFolderId(document.id);
        addDocumentPath(document.id);
      }
    },
    [
      addDocumentPath,
      setCurrentFolderId,
      setDocumentPreview,
      setDocumentSidebar,
      updateUrl,
    ],
  );

  const navigateBack = useCallback(() => {
    setCurrentFolderId(parentFolderId);
  }, [parentFolderId, setCurrentFolderId]);

  const navigateByFolder = useCallback(
    (breadcrumb: Breadcrumb) => {
      setCurrentFolderId(getNextFolderId(breadcrumb));
    },
    [getNextFolderId, setCurrentFolderId],
  );

  const handleSubmitDocumentDelete = () => {
    if (sidebarVisible) setDocumentSidebar(false);
    if (previewVisible) setDocumentPreview(false);
    documentDelete(handleResetState);
  };

  const renderContent = useCallback(() => {
    if (activeLoading || (isSearchAll ? !searchData : !data)) {
      return <Loading />;
    }

    return (
      <PersonalDocumentsTable
        isManager={isManager}
        resetSelectedRows={resetSelectedRows}
        selectAllRows={selectAllRows}
        documents={documents}
        hasPersonalDocumentFilter={hasPersonalDocumentFilter(filter)}
        onDownload={handleDownload}
        setSelectedRows={addSelectedRow}
        onClick={onClick}
        onDelete={handleDelete}
        onRename={handleOpenRenameModal}
        onMove={handleMoveToFolder}></PersonalDocumentsTable>
    );
  }, [
    activeLoading,
    data,
    searchData,
    isSearchAll,
    isManager,
    resetSelectedRows,
    selectAllRows,
    documents,
    filter,
    handleDownload,
    addSelectedRow,
    onClick,
    handleDelete,
    handleOpenRenameModal,
    handleMoveToFolder,
  ]);

  const onSearch = (val: string) => setFilter({ ...filter, documentName: val });

  const isSearchActive = searchScope === 'all' && !!debouncedSearchTerm;

  if (error || (searchError && isSearchActive)) {
    return <ServerError />;
  }

  return (
    <>
      <>
        <Card>
          <Card.Header className="h-auto">
            <GlobalTableFilter
              value={filter.documentName || ''}
              placeholder={t('search')}
              onFilterChange={onSearch}
            />
            {filter.documentName && (
              <ToggleGroup
                value={searchScope}
                buttons={scopeButtons}
                onChange={(val) => {
                  if (val === 'all' || val === 'current') {
                    setSearchScope(val);
                  }
                }}
                variant="secondary-2"
              />
            )}
            <PersonalDocumentsFiltersConnected
              categories={categoryOptionsWithName}
              employees={employeeOptions}
              loading={filterLoading}
              error={filterError}
              filter={filter}
              onChange={setFilter}></PersonalDocumentsFiltersConnected>
          </Card.Header>
          <Card.Header>
            <div className="d-flex gap-3 justify-content-between align-items-center small">
              <div className="d-flex align-items-center">
                <FolderBreadcrumbs
                  breadcrumbs={breadcrumbs}
                  navigateByFolder={navigateByFolder}
                  navigateBack={navigateBack}
                  isLoading={loading}
                />
              </div>
              <div className="d-flex gap-3 align-items-center justify-content-end">
                {canUpload && (
                  <Button
                    onClick={() => setUploadVisible(true)}
                    variant="primary"
                    className="d-flex align-items-center">
                    <FlairIcon icon="file-arrow-up" />
                    <span className="ms-2 small">
                      {t('table.buttons.upload')}
                    </span>
                  </Button>
                )}
                {!isManager && (
                  <StyleIcon
                    variant="outline-primary"
                    className="d-flex align-items-center"
                    onClick={() => setCreateFolderVisible(true)}>
                    <FlairIcon icon="folder-simple-plus" />
                    <span className="ms-2 small">
                      {t('table.buttons.folder')}
                    </span>
                  </StyleIcon>
                )}
              </div>
            </div>
          </Card.Header>
          {renderContent()}
        </Card>
        <SelectionBottomPanel
          selectedCount={getUniqueSelectedRows().length}
          onSelectAll={() => setSelectAllRows(true)}
          onCancel={() => {
            setResetSelectedRows(true);
          }}>
          <TableBottomBanner
            isManager={isManager}
            onDelete={() => handleMassDelete(selectedRows)}
            onMove={() => {
              handleMassMove(selectedRows);
            }}></TableBottomBanner>
        </SelectionBottomPanel>
      </>
      <DocumentDeleteConfirmation
        selectedItems={deleteItems}
        visible={!!deleteItems.length}
        submitInProgress={deleteInProgress}
        onClose={handleDeleteClose}
        onSubmit={handleSubmitDocumentDelete}></DocumentDeleteConfirmation>
      <DocumentPreviewModal
        visible={previewVisible && !!getSelectedDocumentRow()}
        isPersonal={true}
        isManager={isManager}
        onDownload={handleDownload}
        onDelete={() => {
          const row = getSelectedDocumentRow();
          if (!row) {
            return;
          }
          handleDelete(
            {
              id: row.id,
              type: row.typeName,
            },
            row.type,
          );
        }}
        isDeleteModalVisible={!!deleteItems.length}
        document={getSelectedDocumentRow()}
        documentCategories={categoryOptionsWithId}
        onClose={handlePreviewClose}></DocumentPreviewModal>
      <DocumentInfoSidebar
        documentCategories={categoryOptionsWithId}
        visible={sidebarVisible && !!getSelectedDocumentRow()}
        isPersonal={true}
        isManager={isManager}
        onDownload={handleDownload}
        document={getSelectedDocumentRow()}
        onDelete={() => {
          const row = getSelectedDocumentRow();
          if (!row) {
            return;
          }
          handleDelete(
            {
              id: row.id,
              type: row.typeName,
            },
            row.type,
          );
        }}
        onClose={() => {
          handleSideBarClose();
        }}></DocumentInfoSidebar>
      <DocumentFolderModal
        folderName={folderName}
        visible={createFolderVisible}
        createFolder={createFolder}
        createInProgress={createFolderInProgress}
        setFolderName={setFolderName}
        onClose={handleCreateFolderClose}
      />
      <DocumentUploadModalConnected
        employeeId={employeeId}
        onClose={() => setUploadVisible(false)}
        visible={uploadVisible && canUpload}
        categories={categoryOptionsWithId}
        currentFolderId={currentFolderId}
      />
      <DocumentMoveModal
        isCreateFolderModalVisible={createFolderVisible}
        openCreateFolderModal={() => setCreateFolderVisible(true)}
        employeeId={employeeId}
        visible={!!rowsToMove.length}
        currentFolderId={currentFolderId}
        setSelectedFolderId={setSelectedFolderId}
        selectedFolderId={selectedFolderId}
        rowsToMove={rowsToMove}
        onClose={handleMoveToModalClose}
        onSubmit={() => {
          move(handleResetState);
        }}
        moveInProgress={moveInProgress}
      />
      <DocumentRenameModal
        visible={!!rowToRename}
        onClose={handleCloseRenameModal}
        renameInProgress={renameInProgress}
        setName={handleSetNewName}
        rename={rename}
        name={name}
        initialName={rowToRename?.name}
      />
      <NoFilesWarningModal
        visible={showNoFilesModal}
        onClose={() => setShowNoFilesModal(false)}
      />
    </>
  );
};

const Loading: React.FC<React.PropsWithChildren<unknown>> = () => {
  return <SkeletonTable columns={6} rows={5} />;
};

const StyleIcon = styled(Button)`
  &:hover,
  &:focus svg {
    path {
      stroke: ${Theme.color.white};
    }
  }
`;

type NoFilesWarningModalProps = {
  visible: boolean;
  onClose: () => void;
};

export const NoFilesWarningModal: React.FC<
  React.PropsWithChildren<NoFilesWarningModalProps>
> = ({ visible, onClose }) => {
  const t = useNamespacedTranslation(i18Path);

  return (
    <Modal
      show={visible}
      onHide={onClose}
      centered
      dialogClassName="break-modal-440">
      <Modal.Header closeButton className="card-header border-0">
        <div className="mt-4 d-flex flex-column align-items-center justify-content-center">
          <FlairIcon
            size="xxl"
            icon="alert-circle-outline"
            className="text-warning"
          />
          <h2 className="mt-2 card-header-title text-center">
            {t('noFilesWarningModal.title')}
          </h2>
        </div>
      </Modal.Header>
      <Modal.Body className={'pt-4'}>
        <div>
          <div>
            <p className="text-center">
              {t('noFilesWarningModal.description')}
            </p>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
